import { useContext, useEffect, useState } from 'react';
import { Image, Center, Box, Text, Spinner } from '@chakra-ui/react';
import { getLogo } from 'utils/marque';
import { useHistory } from 'react-router-dom';
import { AuthContext, auth } from 'auth/AuthContextProvider';
import { getParameterByNameOnUrl } from 'utils/helpers';
import { Button } from 'components/Button';

/*
  Page d'atterrissage du SSO partenaire : https://partenaire.<env>.monemprunt.com/sso?token=<custom token>

  Le token de l'URL est un "custom token" firebase généré par l'API partenaire (sur le tenant de
  l'environnement, avec l'uid du compte firebase de l'utilisateur). Il n'est jamais envoyé à notre
  API : il est échangé ici contre un ID token via signInWithCustomToken, et c'est cet ID token que
  l'AuthContextProvider place ensuite en Authorization sur les appels /API.
*/

const MESSAGE_LIEN_INCOMPLET =
  'Ce lien de connexion est incomplet. Rapprochez-vous de votre entreprise pour en obtenir un nouveau.';
const MESSAGE_LIEN_INVALIDE =
  "Ce lien de connexion n'est plus valide ou a expiré. Rapprochez-vous de votre entreprise pour en obtenir un nouveau.";
const MESSAGE_ERREUR_TECHNIQUE =
  'Un problème technique est survenu pendant la connexion. Merci de réessayer dans quelques instants.';

/**
 * Traduit une erreur firebase en message affichable. Un custom token invalide, expiré ou émis
 * sur un autre tenant que celui de l'environnement remonte sur les codes ci-dessous.
 */
const messagePourErreur = (code?: string) => {
  switch (code) {
    case 'auth/invalid-custom-token':
    case 'auth/custom-token-mismatch':
    case 'auth/user-disabled':
    case 'auth/user-not-found':
      return MESSAGE_LIEN_INVALIDE;
    default:
      return MESSAGE_ERREUR_TECHNIQUE;
  }
};

type AuthSsoProps = {
  messageErreur?: string | null;
};

export const AuthSso = ({ messageErreur = null }: AuthSsoProps) => {
  const history = useHistory();

  return (
    <>
      <Box position="fixed" width="100%" bottom="0" right="0">
        <Image
          float="right"
          width={{ base: '100%', lg: '70%' }}
          src="/login_background.svg"
        ></Image>
      </Box>
      <Box
        margin="0px"
        padding="0px"
        width="100%"
        height="100%"
        backgroundColor="#E8F4F4"
        paddingTop="100px"
      >
        <Box
          position="relative"
          marginLeft={{ base: 'calc( 50% - 175px )', lg: '200px' }}
          width="350px"
        >
          <Text
            fontWeight="900"
            fontSize="26px"
            marginLeft="20px"
            color="white"
            backgroundColor="#349FA1"
            padding="5px"
            transform="skew(-0.00turn, -5deg)"
            width="fit-content"
          >
            CONNECTION
          </Text>
          <Text
            fontWeight="900"
            fontSize="26px"
            marginLeft="90px"
            marginTop="5px"
            color="white"
            backgroundColor="#F8B632"
            padding="5px"
            transform="skew(-0.00turn, -5deg)"
            width="fit-content"
          >
            À VOTRE ESPACE
          </Text>

          <Box
            marginLeft={'0px'}
            marginTop="40px"
            padding="20px"
            width="350px"
            height="540px"
            zIndex="1"
            backgroundColor="white"
            borderRadius="-webkit-border-radius: 10px; -moz-border-radius: 10px; border-radius: 10px;"
            boxShadow="0px 0px 30px 00px rgba(0,0,0,0.1)"
            textAlign="center"
          >
            <Center marginBottom="10px">
              <Image maxWidth="100%" marginWidth="auto" src={getLogo()}></Image>
            </Center>
            {messageErreur ? (
              <>
                <Center>
                  <Text as="h3" fontSize={{ base: 'xl', xxl: '2xl' }} fontWeight="semibold" mb={6}>
                    Connexion impossible
                  </Text>
                </Center>
                <Center>
                  <Text color="gray.600" mb={8}>
                    {messageErreur}
                  </Text>
                </Center>
                <Center>
                  <Button onClick={() => history.replace('/login')}>Aller à la connexion</Button>
                </Center>
              </>
            ) : (
              <>
                <Center>
                  <Text as="h3" fontSize={{ base: 'xl', xxl: '2xl' }} fontWeight="semibold" mb={6}>
                    Authentification SSO...
                  </Text>
                </Center>
                <Center>
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="blue.500"
                    size="xl"
                  />
                </Center>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export const AuthSsoRoute = () => {
  const { token } = useContext(AuthContext);
  const history = useHistory();
  const [messageErreur, setMessageErreur] = useState<string | null>(null);

  // lus une seule fois au montage : l'URL est réécrite dès la connexion établie
  const [customToken] = useState(() => getParameterByNameOnUrl('token'));
  // ancien format de lien : l'échange est fait par l'AuthContextProvider, ici on ne fait qu'attendre
  const [customTokenLegacy] = useState(() => getParameterByNameOnUrl('urlCustomToken'));

  useEffect(() => {
    if (!customToken) {
      if (!customTokenLegacy) {
        setMessageErreur(MESSAGE_LIEN_INCOMPLET);
      }
      return;
    }

    let annule = false;
    // On repart d'une session vierge : le lien SSO peut arriver alors qu'un autre compte est
    // déjà connecté dans ce navigateur, sinon on redirigerait avec l'ancienne identité.
    auth
      .signOut()
      .then(() => auth.signInWithCustomToken(customToken))
      .then(() => {
        if (annule) return;
        // replace et pas push : le custom token ne doit rester ni dans l'URL ni dans l'historique
        history.replace('/home');
      })
      .catch((erreur) => {
        if (annule) return;
        console.log("Echec de l'authentification SSO : " + erreur?.code, erreur);
        setMessageErreur(messagePourErreur(erreur?.code));
      });

    return () => {
      annule = true;
    };
  }, [customToken, customTokenLegacy, history]);

  // lien legacy (?urlCustomToken=) : la connexion est portée par l'AuthContextProvider
  useEffect(() => {
    if (!customToken && token !== '') {
      history.replace('/home');
    }
  }, [customToken, token, history]);

  return <AuthSso messageErreur={messageErreur} />;
};
